import { App, Button, Image, Space, Switch, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import SearchInput from "../../components/ui/SearchInput";
import { deleteMaxQuantityRestriction, getProductQuantities } from "../../redux/actions/products";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { formatCount, getProductImageUrl } from "../../utils";
import { PLACEHOLDER_IMAGE } from "../../constants";
import { Eye } from "lucide-react";
import AddQuantityRestriction from "./Add";
import clsx from "clsx";
import dayjs from "dayjs";

const QuantityLimit = () => {
    const { modal: antdModal } = App.useApp();
    const [products, setProducts] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        totalRecords: 0,
        totalPages: 0,
    });
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [showThumbnails, setShowThumbnails] = useState(false);

    const getMaxQuantities = async (body) => {
        setLoading(true);
        const res = await getProductQuantities(body);
        if (res?.ack) {
            setProducts(res.products_quantities || []);
            setPagination((prev) => ({
                ...prev,
                current: body?.page || 1,
                totalRecords: res.total_count,
                totalPages: Math.ceil(res.total_count / prev.pageSize),
            }));
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        const res = await deleteMaxQuantityRestriction({ id });
        if (res?.ack) {
            getMaxQuantities({ page: pagination.current, searchText: search });
        }
    };

    const columns = [
        {
            dataIndex: 'thumbnail',
            key: 'thumbnail',
            align: 'center',
            width: 60,
            hidden: !showThumbnails,
            render: (_, record) => {
                if (!showThumbnails) return;
                const productImg = getProductImageUrl(record);
                const items = products?.map(
                    (item) => getProductImageUrl(item) || PLACEHOLDER_IMAGE
                );

                return (
                    <Image.PreviewGroup items={items}>
                        <Image
                            src={productImg}
                            alt={record?.product_name}
                            width={50}
                            height={50}
                            fallback={PLACEHOLDER_IMAGE}
                            preview={{
                                mask: <Eye size={16} />,
                            }}
                        />
                    </Image.PreviewGroup>
                );
            },
        },
        {
            title: 'Code',
            dataIndex: 'product_code',
            key: 'product_code',
        },
        {
            title: 'Name',
            dataIndex: 'product_name',
            key: 'product_name',
        },
        {
            title: 'Max Quantity',
            dataIndex: 'max_quantity',
            key: 'max_quantity',
            render: (text) => formatCount(text),
        },
        {
            title: 'Created By',
            dataIndex: 'added_by',
            key: 'added_by',
        },
        {
            title: 'Created At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            render: (text) => text && dayjs(text).format('MM-DD-YYYY HH:mm:ss'),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'end',
            fixed: 'right',
            render: (text, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        color="primary"
                        variant="filled"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setModalData(record);
                            setIsModalOpen(true);
                        }}
                    />
                    <Button
                        size="small"
                        color="danger"
                        variant="filled"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                            antdModal.confirm({
                                centered: true,
                                title: 'Delete Product Restriction',
                                content: 'Are you sure you want to delete this product quantity restriction?',
                                okText: 'Yes, Delete',
                                okButtonProps: {
                                    color: 'red',
                                    variant: 'solid',
                                    size: 'medium',
                                },
                                cancelText: 'Cancel',
                                cancelButtonProps: {
                                    variant: 'outlined',
                                    color: 'danger',
                                    size: 'medium',
                                },
                                onOk: () => handleDelete(record.id),
                            });
                        }}
                    />
                </Space>
            ),
        },
    ]

    useEffect(() => {
        getMaxQuantities({ page: 1 });
    }, []);

    return (
        <div className='product-restrictions-page'>
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className="page-title">
                        Quantity Limit
                    </Typography.Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Add Limit
                    </Button>
                </div>

                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                    <SearchInput
                        onSearch={(searchText) => {
                            setSearch(searchText);
                            getMaxQuantities({ searchText, page: 1 });
                        }}
                    />
                    <Space align="center" size={4}>
                        <Typography.Text className="form-item-label">Show Thumbnails</Typography.Text>
                        <Switch
                            checked={showThumbnails}
                            onChange={() => setShowThumbnails((prev) => !prev)}
                            disabled={!products?.length}
                        />
                    </Space>
                </div>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.product_netsuite_id}
                dataSource={products}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.totalRecords,
                    showSizeChanger: false,
                    responsive: true,
                    size: 'small',
                    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
                    onChange: (page) =>
                        getMaxQuantities({ page, searchText: search }),
                }}
                scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                components={{
                    body: {
                        cell: ({ className, ...rest }) => (
                            <td
                                {...rest}
                                className={!!products?.length && clsx(className, 'py-1', {
                                    'py-0': showThumbnails,
                                })}
                            />
                        ),
                    },
                }}
            />

            {isModalOpen && (
                <AddQuantityRestriction
                    open={isModalOpen}
                    data={modalData}
                    onClose={() => {
                        setIsModalOpen(false);
                        setModalData(null);
                    }}
                    afterCreate={() => getMaxQuantities({ page: pagination.current, searchText: search })}
                />
            )}
        </div>
    )
}

export default QuantityLimit